import React, {useEffect, useRef} from 'react';
import AttendanceDayRecord from "./AttendanceDayRecord";
/**
 * @Email : kpozos@ibl.mx
 * @Author: Karen Yunuen Pozos Arias
 * @jobTittle: Full Stack Developer
 * @param {Object} props - Component props.
 * @param {React.RefObject<HTMLElement>} props.headRef - Ref to the associated header cell.
 *        Used to match position and width of the day column dynamically.
 * @param {string} props.color - Base color for styling attendance entries (e.g., hex code, CSS variable).
 * @param {Array<{ day: string }>} [props.currentDays=[]] - List of days to display.
 *        Each object must contain a `day` property (e.g., "Mon 09", "2025-12-09").
 * @param {Array<Object>} [props.results=[]] - List of user attendance data.
 *        Each user object should contain either:
 *          - `days`: array of day-specific records, OR
 *          - `attendances`: fallback array if `days` is absent.
 *        Expected structure per user: `{ id?: string, userId?: string, days?: Array<{ day: string, entries: Array<Object> }>, attendances?: Array<...> }`
 * @param {string} props.model - Model name used to resolve dynamic theming variables (e.g., for color mapping).
 *
 */

// kept outside the component so the default keeps a stable identity across renders
const NOOP = () => {};

const AttendanceDayContainer = ({ headRef, color, currentDays=[], results=[] ,model, onRendered=NOOP}) => {
    const ref = useRef(null);
    const overflowRef = useRef(null);

    useEffect(() => {
        if (!headRef?.current || !ref.current || !overflowRef.current) {
            // nothing to place: report anyway so callers waiting on this do not hang
            onRendered();
            return;
        }

        const updatePositionAndSize = () => {
            // the refs can be detached by the time this runs: it is called from
            // requestAnimationFrame, the ResizeObserver and the resize listener
            if (!headRef?.current || !ref.current || !overflowRef.current) return;
            if (!headRef.current.offsetParent) return;

            const columnRect = headRef.current.getBoundingClientRect();
            const parentRect = ref.current.offsetParent?.getBoundingClientRect?.() ?? { top: 0, left: 0 };

            const top = columnRect.top - parentRect.top;
            const left = columnRect.left - parentRect.left;
            const width = headRef.current.offsetWidth;

            ref.current.style.top = `${top-13}px`;
            ref.current.style.left = `${left}px`;
            overflowRef.current.style.width = `${width}px`;
        };

        requestAnimationFrame(() => {
            updatePositionAndSize();
            // the columns are placed but not painted yet: report on the next frame
            requestAnimationFrame(() => onRendered());
        });

        const resizeObserver = new ResizeObserver(updatePositionAndSize);
        resizeObserver.observe(headRef.current);

        window.addEventListener('resize', updatePositionAndSize);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updatePositionAndSize);
        };
        // depends on the arrays themselves, not on their length: consecutive pages carry the same
        // 20 users over the same 30 days, so a length based effect would never run again and the
        // column would keep the previous placement while onRendered was never reported
    }, [headRef, currentDays, results, onRendered]);

    const getUserEntriesByDay = (user, day) => {
        const sourceArray = user.days ?? user.attendances ?? [];
        const data = sourceArray.find((d) => d.day === day);
        const userId =  user.userId ||  user.id
        return {
            userId: userId,
            entries: data?.entries || [data] || [],
        };
    };

    return (
        <div ref={ref} className={"attendance-day-record-container"}>
            <div ref={overflowRef} className="overflow-auto">
                <div className="d-flex flex-nowrap">
                    {currentDays.map((row) => {
                        const day = row?.day;
                        return  (
                            <AttendanceDayRecord
                                key={day}
                                day={day}
                                model={model}
                                color={color}
                                rows={results?.map((user) => getUserEntriesByDay(user, day))}
                            />
                        )
                    })}
                </div>
            </div>
        </div>
    );
};

export default AttendanceDayContainer;
